import {
  buildOverrideSegment,
  joinFragments,
  overrideSegmentIdsTouchedBy,
  phraseLead,
} from '@src/stores-v2/translationSpanEdit';
import { joinPhraseValues } from '@src/utility/strings';

import type { OverrideOrigin } from '@src/stores-v2/translationSpanEdit';
import type {
  ParentPhrase,
  TranslationPhrase,
} from '@src/types.ts/translation';

// PLAT-11655 — the "absorb on confirm" write, decided as pure data.
//
// A reviewer corrects a span that OVERLAPS existing corrections. Rather than
// refusing (and making them undo first), the corrections the span touches are
// folded into the new one. This module decides WHAT the resulting records are;
// `TranslationStore.absorbIntoOverrideSegment` performs the MobX write and the
// save. Kept out of the store because the store is an observable state cache —
// see CLAUDE.md "Layer Responsibilities" ("no heavy algorithms/business logic")
// — and co-located with the feature, the same way `savedSegmentCombine.ts`
// keeps clip-combine logic out of `AIClipProducer.ts`.
//
// Pure: no MobX, no React, no IO. Every input is plain translation data, so the
// rule is unit-testable without a store, a cache generation or a save path.

/**
 * Invariants the planned write must keep (the reason this is one write and one
 * save, not a sequence of edits):
 *
 * - Exactly one ACTIVE correction covers any source word — corrections are
 *   never nested. The absorbed parents leave in the SAME write that adds the
 *   new one, which is also why the new `o_<anchor>` id may legitimately re-mint
 *   an absorbed correction's id.
 * - No reviewer text is lost: the absorbed corrections' fragments outside the
 *   covered run survive inside the new value.
 * - No machine text is lost: every machine original an absorbed correction had
 *   retired is RE-POINTED at the new correction, never reactivated, so ONE undo
 *   restores the machine text across the whole widened span.
 * - A correction that retired NO machine original cannot be absorbed safely
 *   (undo would have nothing to restore), so the whole write is refused before
 *   anything is written rather than half-applied.
 * - The new correction's span is the UNION of what `buildOverrideSegment`
 *   derived from the fragments it is authored over AND every absorbed
 *   correction's own recorded span. The build is not the last word on coverage:
 *   a correction that retired fewer originals than its span claims would
 *   otherwise leave the union short of the words it owned.
 *
 * The typed text is spliced over the covered run by the caller (the hook)
 * before it arrives here; this module only wraps the absorbed corrections'
 * fragments that sit OUTSIDE the covered run around it. A fragment is kept by
 * exactly one of the two, so no text is composed twice.
 */
export type AbsorbPlan =
  /** The span covers no active fragment at all — nothing can be written. */
  | { kind: 'nothing-covered' }
  /** The span touches no correction: an ordinary create, not an absorb. */
  | { kind: 'plain-create' }
  /** The span sits entirely inside one correction: the re-edit path. */
  | { kind: 're-edit'; segmentId: string; composedValue: string }
  /** Refused: these corrections retired no machine original to restore. */
  | { kind: 'unrestorable'; unrestorable: string[] }
  /** The absorb itself, as the records the write needs. */
  | {
      kind: 'absorb';
      /** The corrections being folded in (their parent records leave). */
      absorbedIds: string[];
      /** The new correction's text: absorbed pieces before + typed + after. */
      composedValue: string;
      /** The new correction's parent record. */
      segment: ParentPhrase;
      /** Retired copies of `authoredOver`, in lead order, pointing at `segment`. */
      retired: TranslationPhrase[];
      /** What the new correction is authored over, paired with `retired`. */
      authoredOver: TranslationPhrase[];
      /** The parent records that stay (everything but the absorbed ones). */
      survivingParents: ParentPhrase[];
      /** What the reviewer saw over the union span before this write. */
      previousActive: TranslationPhrase[];
    };

/**
 * Decide the absorb write for a span, or the path it should be delegated to.
 *
 * `covered` is the ACTIVE fragments the span covers, resolved by the caller so
 * the identity-first covered rule stays in one place. `value` is the reviewer's
 * text, already spliced over the covered run.
 */
export function planAbsorb(
  phrases: readonly TranslationPhrase[],
  parentPhrases: readonly ParentPhrase[],
  covered: readonly TranslationPhrase[],
  value: string,
  origin: OverrideOrigin,
): AbsorbPlan {
  if (covered.length === 0) return { kind: 'nothing-covered' };

  const segmentsById = new Map<string, ParentPhrase>();
  for (const seg of parentPhrases) segmentsById.set(seg.id, seg);
  // The corrections the span touches — the same rule the hook's pending-span
  // resolution uses (`o_` id prefix authoritative, `origin` a fallback).
  const absorbedIds = overrideSegmentIdsTouchedBy(covered, segmentsById);
  if (absorbedIds.length === 0) return { kind: 'plain-create' };

  const absorbedSet = new Set(absorbedIds);
  const coveredPhraseSet = new Set(covered);
  // The absorbed corrections' derived fragments the selection did NOT cover,
  // split into the ones before the span and the ones after it (by lead), so the
  // composed value keeps their text in reading order.
  const spanLead = Math.min(...covered.map(phraseLead));
  const unselectedBefore: TranslationPhrase[] = [];
  const unselectedAfter: TranslationPhrase[] = [];
  for (const p of phrases) {
    if (p.active === false || !p.parentId || !absorbedSet.has(p.parentId))
      continue;
    if (coveredPhraseSet.has(p)) continue;
    (phraseLead(p) < spanLead ? unselectedBefore : unselectedAfter).push(p);
  }
  const composedValue = joinPhraseValues(
    [
      joinFragments(unselectedBefore),
      value.trim(),
      joinFragments(unselectedAfter),
    ].filter(v => v.length > 0),
  );

  // Entirely inside ONE correction ⇒ the re-edit, with the composed value (the
  // reviewer re-typed part of it; the rest survives around it).
  if (
    absorbedIds.length === 1 &&
    covered.every(p => p.parentId === absorbedIds[0])
  ) {
    return { kind: 're-edit', segmentId: absorbedIds[0], composedValue };
  }

  // The machine originals the absorbed corrections retired (still
  // `active: false`, `supersededBy` → an absorbed id). Together with the fresh
  // machine fragments the selection touched, these are the phrases the NEW
  // correction is authored over: `buildOverrideSegment` mints the id from their
  // lead element, unions their coverage, joins their snapshots and produces the
  // retired copies that point at the new id.
  const absorbedOriginals = phrases.filter(
    p => p.supersededBy !== undefined && absorbedSet.has(p.supersededBy),
  );
  // A correction stored before this work can have live child fragments while
  // nothing in `phrases[]` was ever retired under it. Absorbing it would drop
  // its active children with NO machine original registered to put back, so
  // undo could not restore the earlier text. Refuse before anything is written;
  // the caller tells the reviewer, because returning quietly would look like
  // the typed text simply vanished.
  const parentsWithRetired = new Set(
    absorbedOriginals.map(p => p.supersededBy),
  );
  const unrestorable = absorbedIds.filter(id => !parentsWithRetired.has(id));
  if (unrestorable.length > 0) return { kind: 'unrestorable', unrestorable };

  const freshCovered = covered.filter(
    p => !(p.parentId && absorbedSet.has(p.parentId)),
  );
  const authoredOver = [...freshCovered, ...absorbedOriginals];
  const { segment, retired } = buildOverrideSegment(
    authoredOver,
    composedValue,
    origin,
  );
  // Defensive: a correction that retired FEWER originals than its recorded span
  // covers would leave the union short of that coverage (one retiring none at
  // all is refused above). Fold the absorbed parents' recorded span in, so the
  // new correction owns every word they owned. This is why the built segment is
  // not the last word on coverage.
  for (const id of absorbedIds) {
    const parent = segmentsById.get(id);
    if (!parent) continue;
    segment.originalIndices = [
      ...new Set([...segment.originalIndices, ...parent.originalIndices]),
    ].sort((a, b) => a - b);
    if (parent.originalElementIds && parent.originalElementIds.length > 0) {
      segment.originalElementIds = [
        ...new Set([
          ...(segment.originalElementIds ?? []),
          ...parent.originalElementIds,
        ]),
      ];
    }
  }

  const survivingParents = parentPhrases.filter(s => !absorbedSet.has(s.id));

  // What the reviewer saw over the union span before this write — the
  // corrections' fragments and the fresh machine fragments — for the
  // changelog's `previousValue`.
  const previousActive = phrases.filter(
    p =>
      p.active !== false &&
      (coveredPhraseSet.has(p) ||
        (!!p.parentId && absorbedSet.has(p.parentId))),
  );

  return {
    kind: 'absorb',
    absorbedIds,
    composedValue,
    segment,
    retired,
    authoredOver,
    survivingParents,
    previousActive,
  };
}
